import { onUnmounted, ref } from 'vue'

const HOLD_MS = 450
/** Cancel before the browser claims the touch for scrolling. */
const HOLD_SLOP_PX = 8
/** A context-menu long-press cancels the pointer without real movement. */
const STILL_PX = 6

/**
 * Long-press adds copies. A finger that moves or scrolls must not:
 * mobile scroll sends pointercancel, and the card under the finger changes,
 * so the button itself often never sees the drag.
 */
export function useHoldToFill(onFill: (src: HTMLElement) => void) {
  const holding = ref(false)
  let holdTimer: ReturnType<typeof setTimeout> | null = null
  let skipClick = false
  let skipContext = false
  let startX = 0
  let startY = 0
  let moved2 = 0
  let pointerId = -1
  let srcEl: HTMLElement | null = null
  let scrollEl: HTMLElement | null = null
  let watching = false

  function clearHold(): void {
    if (holdTimer) {
      clearTimeout(holdTimer)
      holdTimer = null
    }
    holding.value = false
    unwatch()
  }

  function finishHold(): void {
    if (!holdTimer || !srcEl) return
    const el = srcEl
    clearTimeout(holdTimer)
    holdTimer = null
    holding.value = false
    skipClick = true
    skipContext = true
    unwatch()
    onFill(el)
  }

  function cancelForScroll(): void {
    skipClick = true
    skipContext = true
    clearHold()
  }

  function onScroll(): void {
    if (holdTimer) cancelForScroll()
  }

  function trackMove(event: PointerEvent): void {
    if (!holdTimer || event.pointerId !== pointerId) return
    const dx = event.clientX - startX
    const dy = event.clientY - startY
    const dist2 = dx * dx + dy * dy
    if (dist2 > moved2) moved2 = dist2
    if (dist2 > HOLD_SLOP_PX * HOLD_SLOP_PX) cancelForScroll()
  }

  function trackEnd(event: PointerEvent): void {
    if (event.pointerId !== pointerId) return
    if (event.type === 'pointercancel' && holdTimer) {
      if (moved2 > STILL_PX * STILL_PX) cancelForScroll()
      return
    }
    clearHold()
  }

  function watch(from: HTMLElement): void {
    scrollEl = scrollParent(from)
    scrollEl?.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('pointermove', trackMove, true)
    window.addEventListener('pointerup', trackEnd, true)
    window.addEventListener('pointercancel', trackEnd, true)
    watching = true
  }

  function unwatch(): void {
    if (!watching) return
    scrollEl?.removeEventListener('scroll', onScroll)
    window.removeEventListener('pointermove', trackMove, true)
    window.removeEventListener('pointerup', trackEnd, true)
    window.removeEventListener('pointercancel', trackEnd, true)
    scrollEl = null
    watching = false
  }

  function begin(event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      skipContext = false
      return
    }
    clearHold()
    skipClick = false
    skipContext = false
    moved2 = 0
    pointerId = event.pointerId
    srcEl = event.currentTarget as HTMLElement
    startX = event.clientX
    startY = event.clientY
    holding.value = true
    watch(srcEl)
    holdTimer = setTimeout(() => {
      finishHold()
    }, HOLD_MS)
  }

  function consumeClick(): boolean {
    if (!skipClick) return false
    skipClick = false
    return true
  }

  function consumeContext(): 'fill' | 'skip' | 'menu' {
    if (holdTimer) {
      finishHold()
      // This event is the long-press itself, so don't swallow the next one.
      skipContext = false
      return 'fill'
    }
    if (skipContext) {
      skipContext = false
      return 'skip'
    }
    return 'menu'
  }

  onUnmounted(() => clearHold())

  return { holding, begin, consumeClick, consumeContext }
}

function scrollParent(start: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = start.parentElement
  while (node) {
    const oy = getComputedStyle(node).overflowY
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay')
      && node.scrollHeight > node.clientHeight + 1
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}
